/**
 * English UI strings — the shape source of truth for all locales.
 *
 * Rules:
 * - `strings.es.ts` is typed `typeof en`, so a missing or extra key there is a
 *   compile error. Do NOT add `as const` here (it would force literal types on
 *   translations) and keep the Spanish file a direct object literal.
 * - Values may never be rendered through html-react-parser / markdown
 *   pipelines — JSX text/attribute positions only.
 * - `%1s`, `%2s`… placeholders are numbered (see formatString) so translations
 *   can reorder them freely.
 */
export const en = {
  signOut: "Sign Out",
  accessDenied: "Access Denied",
  accessDeniedMessage:
    "RePath Web access is unavailable for your profile. Please download the RePath Mobile app for iOS or Android.",
  checkInPhotoNote:
    "When taking your photo, please ensure you have adequate lighting.",
  checkInModalTitle: "Check-In Photo",
  questionsModalTitle: "Questions",
  updateAnswersModalTitle: "Update Answers",
  termsModalTitle: "Terms of Use",
  termsModalText:
    "Welcome to RePath! Please review and accept our Terms of Use to continue",
  acceptTermsBtn: "Accept Terms",
  questionsModalText: "Please submit your answers to the following questions",
  updateAnswersModalText:
    "You can update your answers to the following questions at any time",
  submitting: "Submitting...",
  submitAnswers: "Submit Answers",
  save: "Save",
  start: "Start",
  answerNow: "Answer Now",
  nextQuestionsDate: "Next Questions Date",
  questionsDue: "Check-In Questions Due!",
  loading: "Loading...",
  questionAnswers: {
    yes: "Yes",
    no: "No",
    yesNotesPlaceholder: "Please explain...",
    textPlaceholder: "Type your answer...",
  },
  reportForm: {
    title: "Report Form",
    questionList: "Question List",
    closeLabel: "Close report form",
    welcomeHeading: "Let’s get ready to meet with your officer.",
    welcomeBody:
      "For your appointment, you need to answer some required questions to keep your officer up to date.",
    welcomeHint: "Need to go back? Use the Question List.",
    start: "Start",
    // `%1s` = current step, `%2s` = total questions, e.g. "Question 1/24"
    stepCounter: "Question %1s/%2s",
    getStarted: "Get Started",
    workflow: {
      saveContinue: "Save & Continue",
      // Steps back from a detailed-information screen to the question's root
      // choice, so a participant can change their answer without going through
      // the Question List menu (EHA-903).
      changeAnswer: "Change my answer",
      // Removes an extra entry from a repeatable structured form (e.g. a vehicle).
      remove: "Remove",
      // Generic fallback for a repeatable form's "add entry" control when neither
      // the server nor the catalog supplies a workflow-specific label.
      addAnother: "Add another",
      // Generic fallback for a DIRECT_LIST skip control (householdMembers'
      // "I live alone - skip" comes from the catalog).
      skip: "Skip",
      // Field-format validation messages (IHA input-validation follow-up):
      // shown inline under a Detailed Information field when it has a value
      // but that value doesn't match the field's expected format.
      errors: {
        email: "Enter a valid email address (e.g. name@example.com).",
        vehicleYear: "Enter a valid 4-digit year.",
        ownerName: "Enter a name — it can't be only numbers.",
      },
    },
    questionMenu: {
      // Names the menu dialog (its accessible name for screen readers) — distinct
      // from the wizard's own "Report Form" title so the two aren't confused.
      title: "Question List",
      // Distinct from the toolbar's close (which exits the wizard) — this only
      // dismisses the menu, so it needs its own accessible name.
      closeLabel: "Close question list",
      // Section header for questions with no default section (custom / admin-added).
      generalSection: "General Questions",
      statusDone: "Done",
      statusCurrent: "You are here",
      statusNotStarted: "Not started",
    },
    finalStatement: {
      topic: "Submission",
      heading: "Are you ready to submit this report? Confirm and submit.",
      firstNameLabel: "First Name",
      lastNameLabel: "Last Name",
      submit: "Submit Report",
      submitting: "Submitting…",
      successHeading: "Report submitted",
      successBody: "Your answers have been sent to your officer.",
      errorHeading: "Submission failed",
      errorBody:
        "We couldn’t submit your report. Your answers are saved — please try again.",
      retry: "Try Again",
      // Shown under a name field when it contains only digits/punctuation —
      // a signature name can't be purely numeric.
      nameInvalid: "Enter a name — it can't be only numbers.",
    },
  },
  sessionTimeout: {
    heading: "Are you still there?",
    body: "Your session will end soon and you will be signed out. Seconds remaining:",
    continue: "Continue my session",
  },
  pageNotFound: "Page not found",
  skipToContent: "Skip to main content",
  pages: {
    dashboard: "Home",
    appointments: "Appointments",
    documents: "Documents",
    termsOfUse: "Terms of Use",
    officerProfile: "My Officer",
    resources: "Resources",
    resourceDetail: "Resource details",
  },
  navbar: {
    // Accessible names — asserted verbatim by unit and e2e tests; values for
    // `en` must stay byte-identical to the pre-i18n literals.
    openSidebar: "open sidebar",
    closeSidebar: "Close navigation menu",
    capturePhoto: "Take photo",
    signOutAria: "Sign out of your account",
    navigationMenu: "Navigation menu",
  },
  login: {
    usernameLabel: "Phone Number or Email",
    usernamePlaceholder: "Enter your phone number or email",
    // Static validation text — never echoes what the user typed.
    phoneValidation:
      "Enter a 10-digit US phone number, or an international number including its country code (e.g. +63 917 555 1234).",
  },
  countdown: {
    today: "today",
    oneDay: "1 day",
    // `%1s` = number of days
    days: "%1s days",
    oneDayAgo: "1 day ago",
    daysAgo: "%1s days ago",
    takesPlaceToday: "Takes place today",
    // `%1s` = a FUTURE countdown phrase (e.g. "3 days") — never a past one
    takesPlaceIn: "Takes place in %1s",
    // `%1s` = a past phrase (e.g. "1 day ago" / "hace 1 día")
    takesPlacePast: "Took place %1s",
  },
  // `%1s` = date, `%2s` = time — word order differs per language
  dateAtTime: "%1s at %2s",
  // Appointment date-times. `%1s` = short weekday, `%2s` = short month,
  // `%3s` = day of month, `%4s` = start time, `%5s` = end time.
  // Word order and separators differ per language.
  dateTimeAt: "%1s, %2s %3s @ %4s",
  dateTimeAtRange: "%1s, %2s %3s @ %4s - %5s",
  documents: {
    myDocuments: "My Documents",
    uploadDocuments: "Upload Documents",
    chooseFileLabel: "Choose a document to upload",
    fileType: "Type",
    fileName: "Name",
    uploadDate: "Upload Date",
    action: "Action",
    downloadBtn: "Download",
    deleteBtn: "Delete",
    dragDropText: "Drag & drop your files here",
    loadingDocuments: "Loading Documents...",
    loadDocsError: "Error loading documents: ",
    downloadDocError: "Error downloading document: ",
  },
  dashboard: {
    // `%1s` = the participant's first name (or "..." while their case loads)
    greeting: "👋 Hello, %1s",
    greetingFallback: "👋 Hello, there",
    checkInHeader: "Check-In",
    nextCourtDate: "Next Court Date",
    nextAppointmentDate: "Next Appointment Date",
    // `%1s` = how many upcoming items there are
    seeAll: "See All (%1s)",
  },
  // Bold labels on appointment/court detail rows (colon included)
  appointmentLabels: {
    what: "What:",
    when: "When:",
    where: "Where:",
    instructions: "Instructions:",
  },
  appointments: {
    apptsHeader: "Appointments",
    courtHeader: "Court",
    courtTileTitle: "Court Date",
    appointmentTileTitle: "Appointment",
    upcomingCourtDate: "Upcoming court date on",
    upcomingAppt: "Upcoming appointment on",
    noUpcomingAppts: "No upcoming appointments",
    noUpcomingCourtDates: "No upcoming court dates",
    apptsTimeFrame: "%1s Between %2s - %3s",
  },
  toast: {
    generalError: "An error has occurred, please try again.",
    faceNotRecognized: "Face not recognized, please try again.",
    multipleFacesDetected: "Multiple faces detected, please try again.",
    noFaceDetected: "No face detected, please try again.",
    loadCaseError: "Error loading Participants case: ",
    checkInPhotoSuccess: "Check-In photo submitted",
    checkInPhotoError: "Failed to submit the Check-In photo, please try again.",
    questionAnswersSuccess: "Question answers successfully submitted",
    questionAnswersError: "Error submitting question answers!",
    uploadDocumentSuccess: "Document successfully uploaded",
    uploadDocumentError: "Error uploading document!",
    loading: "Please wait...",
    loadAppointmentsError: "Error loading appointments",
    loadTermsAndConditionsError:
      "Failed to load the Terms of Use, please refresh to try again.",
    acceptTermsSuccess: "Terms of Use accepted",
    acceptTermsError: "Failed to accept Terms of Use, please try again.",
  },
  pageContentLoading: "Loading",
  // `%1s` = the current year
  copyright: "Copyright © eHawk Solutions %1s",
  chat: {
    navTitle: "Chat",
    // Composed into "{Loading} {loadingLabel}..." while the page loads
    loadingLabel: "Chat",
    headerFallback: "Your Officer",
    emptyState: "No messages yet. Send a message to your officer.",
    inputPlaceholder: "Type a message",
    inputLabel: "Message",
    send: "Send",
    sendFailed: "Message failed to send.",
    retry: "Retry",
    typing: "Typing…",
    loadError: "Unable to load messages, please try again.",
    messageHistory: "Message history",
    loadingOlder: "Loading earlier messages",
    today: "Today",
    yesterday: "Yesterday",
    unreadMessages: "unread messages",
  },
  resources: {
    loading: "Loading resources...",
    loadError: "Unable to load resources. Please try again later.",
    empty: "No resources available.",
    categoryLabel: "Category",
    allCategories: "All",
    keywordsLabel: "Keywords:",
    // `%1s` = number of resources shown, `%2s` = category name
    resultCountOne: "Showing 1 resource",
    resultCountMany: "Showing %1s resources",
    resultCountOneInCategory: "Showing 1 resource in %2s",
    resultCountManyInCategory: "Showing %1s resources in %2s",
    searchLabel: "Search resources",
    clearSearch: "Clear search",
    clearSearchText: "Clear search text",
    searchAllCategories: "Search all categories",
    // `%1s` = number of matches, `%2s` = category name, `%3s` = search text
    searchCountOne: "1 resource matches “%3s”",
    searchCountMany: "%1s resources match “%3s”",
    searchCountOneInCategory: "1 resource in %2s matches “%3s”",
    searchCountManyInCategory: "%1s resources in %2s match “%3s”",
    noMatch: "No resources match “%3s”.",
    noMatchInCategory: "No resources in %2s match “%3s”.",
    allResources: "All resources",
    notAvailable: "This resource is no longer available.",
    mainLocation: "Main location",
    otherLocations: "Other locations",
    // `%1s` = the location's position in the list
    locationNumber: "Location %1s",
    // `%1s` = phone number, as the agency entered it
    call: "Call %1s",
    // `%1s` = extension digits
    extension: "ext. %1s",
    // `%1s` = location or resource name
    directionsTo: "Directions to %1s",
    opensInNewTab: "(opens in a new tab)",
  },
};

export type StringsShape = typeof en;
